// Grading Policy Manager Component with Percentage Calculators
// File: src/components/GradingPolicyManager.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Settings,
    Plus,
    Edit3,
    Trash2,
    Eye,
    Download,
    Upload,
    CheckCircle,
    AlertCircle,
    Calculator,
    BookOpen,
    Save,
    X,
    Copy,
    Clock,
    Info,
    Check,
    Percent
} from 'lucide-react';
import gradingPolicyService from '../services/gradingPolicyService';
import { ProgramTypes } from '../models/gradingPolicy';
import {
    useGradingPolicies,
    useGradeCalculation,
    useCreatePolicy,
    useUpdatePolicy,
    useDeletePolicy
} from '../hooks/useGradingPolicies';
import { useAssessment, DEFAULT_LATE_POLICY } from './SharedContext';
import CourseSettings from './CourseSettings';
import WhatIfCalculator from './WhatIfCalculator';

// PolicyForm component for creating and editing grading policies
const POLICY_MANAGER_MAIN_TAB_KEY = 'policyManagerActiveMainTab';
const POLICY_MANAGER_CALCULATOR_TAB_KEY = 'policyManagerCalculatorTab';
const PolicyForm = ({ policy, onSave, onCancel }) => {
    const [formData, setFormData] = useState(policy);
    const supportedProgramTypes = gradingPolicyService.getSupportedProgramTypes();

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleProgramTypeChange = (programType, isChecked) => {
        const existing = formData.associatedProgramTypes || formData.programTypes || [];
        setFormData(prev => ({
            ...prev,
            associatedProgramTypes: isChecked
                ? [...existing, programType]
                : existing.filter(type => type !== programType)
        }));
    };

    const handleGradeScaleChange = (index, field, value) => {
        const newGradeScale = [...formData.gradeScale];
        newGradeScale[index] = { ...newGradeScale[index], [field]: value };
        setFormData(prev => ({ ...prev, gradeScale: newGradeScale }));
    };

    const addGradeScaleRow = () => {
        setFormData(prev => ({
            ...prev,
            gradeScale: [...prev.gradeScale, { letter: '', gpa: 0, minPercentage: 0, maxPercentage: 0, passingGrade: false }]
        }));
    };

    const removeGradeScaleRow = (index) => {
        setFormData(prev => ({
            ...prev,
            gradeScale: formData.gradeScale.filter((_, i) => i !== index)
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full p-6 overflow-y-auto max-h-[90vh]">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{formData.id ? 'Edit' : 'Create'} Grading Policy</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
                </div>

                <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Policy Name</label>
                            <input
                                type="text"
                                value={formData.name || ''}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="e.g., University Degree Standard 2025"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <div className="flex items-center space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive || false}
                                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                                        className="mr-2"
                                    />
                                    <span className="text-sm">Active</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.isDefault || false}
                                        onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                                        className="mr-2"
                                    />
                                    <span className="text-sm">Default Policy</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            value={formData.description || ''}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows="2"
                            placeholder="A brief description of this policy"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Program Types</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {supportedProgramTypes.map(programType => (
                                <label key={programType.value} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={(formData.associatedProgramTypes || formData.programTypes || []).includes(programType.value) || false}
                                        onChange={(e) => handleProgramTypeChange(programType.value, e.target.checked)}
                                        className="mr-2"
                                    />
                                    <span className="text-sm">{programType.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-md font-medium text-gray-800">Grade Scale</h3>
                            <button
                                onClick={addGradeScaleRow}
                                className="text-sm text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1"
                            >
                                <Plus size={14} />Add Grade Row
                            </button>
                        </div>
                        <div className="space-y-2">
                            <div className="grid grid-cols-6 gap-2 text-xs font-medium text-gray-600 mb-2">
                                <div>Letter</div>
                                <div>Min %</div>
                                <div>Max %</div>
                                <div>GPA</div>
                                <div>Passing</div>
                                <div>Action</div>
                            </div>
                            {formData.gradeScale?.map((grade, index) => (
                                <div key={index} className="grid grid-cols-6 gap-2 items-center">
                                    <input
                                        type="text"
                                        placeholder="A+"
                                        value={grade.letter || ''}
                                        onChange={e => handleGradeScaleChange(index, 'letter', e.target.value)}
                                        className="p-2 border rounded text-sm focus:ring-1 focus:ring-blue-500"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Min %"
                                        value={grade.minPercentage || ''}
                                        onChange={e => handleGradeScaleChange(index, 'minPercentage', parseFloat(e.target.value) || 0)}
                                        className="p-2 border rounded text-sm focus:ring-1 focus:ring-blue-500"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Max %"
                                        value={grade.maxPercentage || ''}
                                        onChange={e => handleGradeScaleChange(index, 'maxPercentage', parseFloat(e.target.value) || 0)}
                                        className="p-2 border rounded text-sm focus:ring-1 focus:ring-blue-500"
                                    />
                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder="GPA"
                                        value={grade.gpa || ''}
                                        onChange={e => handleGradeScaleChange(index, 'gpa', parseFloat(e.target.value) || 0)}
                                        className="p-2 border rounded text-sm focus:ring-1 focus:ring-blue-500"
                                    />
                                    <label className="flex items-center justify-center">
                                        <input
                                            type="checkbox"
                                            checked={grade.passingGrade || false}
                                            onChange={e => handleGradeScaleChange(index, 'passingGrade', e.target.checked)}
                                            className="w-4 h-4"
                                        />
                                    </label>
                                    <button
                                        onClick={() => removeGradeScaleRow(index)}
                                        className="text-red-500 hover:text-red-700 flex justify-center"
                                        disabled={formData.gradeScale?.length <= 1}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex gap-3 mt-6">
                    <button
                        onClick={() => onSave(formData)}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                        <Save size={16} />Save Policy
                    </button>
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

const GradingPolicyManager = () => {
    // Use TanStack Query hooks
    const { data: policies = [], isLoading: loading } = useGradingPolicies();
    const gradeCalculation = useGradeCalculation();
    const createPolicyMutation = useCreatePolicy();
    const updatePolicyMutation = useUpdatePolicy();
    const deletePolicyMutation = useDeletePolicy();

    const {
        currentLatePolicy,
        setCurrentLatePolicy,
        customLatePolicies,
        saveCustomLatePolicy,
        updateCustomLatePolicy,
        deleteCustomLatePolicy,
        loadLatePoliciesFromStorage,
        gradeBook,
        classList,
        sharedRubric
    } = useAssessment();

    const normalizePenaltiesByDay = useCallback((policy) => {
        if (policy?.penaltiesByDay && Object.keys(policy.penaltiesByDay).length > 0) {
            return policy.penaltiesByDay;
        }

        if (policy?.levels) {
            const converted = Object.entries(policy.levels).reduce((acc, [_, level], index) => {
                const day = Number(level.day ?? index + 1);
                const penalty = level.multiplier > 1
                    ? Math.min(level.multiplier, 1)
                    : Math.min(Math.max(1 - (level.multiplier ?? 1), 0), 1);

                if (!Number.isNaN(day) && day > 0) {
                    acc[day] = penalty;
                }
                return acc;
            }, {});

            if (Object.keys(converted).length > 0) return converted;
        }

        return DEFAULT_LATE_POLICY.penaltiesByDay;
    }, []);

    // Local component state
    const [editingPolicy, setEditingPolicy] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [testGrade, setTestGrade] = useState('');
    const [testResult, setTestResult] = useState(null);
    const [editingLatePolicy, setEditingLatePolicy] = useState(null);
    const [showLatePolicyForm, setShowLatePolicyForm] = useState(false);
    const [programTypeFilter, setProgramTypeFilter] = useState('All programs');
    const [programDefaults, setProgramDefaults] = useState(() => gradingPolicyService.getProgramPolicyDefaults());

    // New state for main tab navigation
    const [activeMainTab, setActiveMainTab] = useState('policies');

    useEffect(() => {
        const storedMainTab = sessionStorage.getItem(POLICY_MANAGER_MAIN_TAB_KEY);
        if (storedMainTab === 'calculators') {
            setActiveMainTab('calculators');
            sessionStorage.removeItem(POLICY_MANAGER_MAIN_TAB_KEY);
        }
    }, []);

    const selectablePolicies = useMemo(
        () => policies.filter(policy => policy.isActive !== false),
        [policies]
    );

    // Set initial selected policy when policies load
    useEffect(() => {
        if (selectablePolicies.length === 0) return;

        const lastViewed = gradingPolicyService.getLastViewedPolicyId();
        const match = lastViewed
            ? selectablePolicies.find(policy => policy.id === lastViewed)
            : null;

        setSelectedPolicy(match || selectablePolicies[0]);
    }, [selectablePolicies]);

    useEffect(() => {
        setProgramDefaults(gradingPolicyService.getProgramPolicyDefaults());
    }, [policies]);

    const filteredPolicies = useMemo(() => {
        const list = programTypeFilter === 'All programs'
            ? selectablePolicies
            : selectablePolicies.filter(policy =>
                (policy.associatedProgramTypes || policy.programTypes || []).includes(programTypeFilter)
            );

        return list;
    }, [selectablePolicies, programTypeFilter]);

    // Load late policies from localStorage
    useEffect(() => {
        loadLatePoliciesFromStorage();
    }, [loadLatePoliciesFromStorage]);

    const handlePolicySelect = (policy) => {
        setSelectedPolicy(policy);
        gradingPolicyService.setLastViewedPolicyId(policy.id);
    };

    const handleSavePolicy = async (policyData) => {
        try {
            if (editingPolicy?.id) {
                // Update existing policy
                await updatePolicyMutation.mutateAsync({
                    policyId: editingPolicy.id,
                    policyData
                });
            } else {
                // Create new policy
                await createPolicyMutation.mutateAsync(policyData);
            }

            setShowCreateForm(false);
            setEditingPolicy(null);
        } catch (error) {
            console.error('Error saving policy:', error);
            alert('Error saving policy. Please try again.');
        }
    };

    const handleSetDefaultForProgram = (programType, policyId) => {
        const updated = gradingPolicyService.setProgramTypeDefault(programType, policyId);
        setProgramDefaults(updated);
    };

    const handleDeletePolicy = async (policyId) => {
        if (window.confirm('Are you sure you want to delete this policy?')) {
            try {
                await deletePolicyMutation.mutateAsync(policyId);
                if (selectedPolicy?.id === policyId) {
                    setSelectedPolicy(null);
                }
            } catch (error) {
                console.error('Error deleting policy:', error);
                alert('Error deleting policy. Please try again.');
            }
        }
    };

    const testGradeInputRef = useRef(null);

    const handleTestGradeChange = (value) => {
        // Keep the calculator input responsive by sanitizing text instead of relying on the
        // native number input, which was forcing the field to blur after a single digit.
        const sanitized = value
            .replace(/[^0-9.]/g, '')   // only digits and decimal point
            .replace(/(\..*)\./g, '$1'); // prevent multiple decimals

        setTestGrade(sanitized);

        // Re-focus the input after React re-renders so the user can continue typing without
        // a second click. Also restore the caret to the end of the text.
        if (testGradeInputRef.current) {
            requestAnimationFrame(() => {
                if (!testGradeInputRef.current) return;
                const cursor = sanitized.length;
                testGradeInputRef.current.focus({ preventScroll: true });
                testGradeInputRef.current.setSelectionRange(cursor, cursor);
            });
        }
    };

    const testGradeCalculation = async () => {
        if (!testGrade || !selectedPolicy) return;

        const percentage = parseFloat(testGrade);
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
            setTestResult({ error: 'Please enter a valid percentage (0-100)' });
            return;
        }

        try {
            const result = await gradeCalculation.mutateAsync({
                percentage,
                policyId: selectedPolicy.id,
                programType: (selectedPolicy.programTypes || selectedPolicy.associatedProgramTypes || [])[0]
            });
            setTestResult(result);
        } catch (error) {
            setTestResult({ error: error.message });
        }
    };

    const PolicyCard = ({ policy, isSelected, onClick }) => (
        <div
            className={`p-4 border rounded-lg cursor-pointer transition-all ${isSelected
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
            onClick={onClick}
        >
            <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-gray-800">{policy.name}</h3>
                <div className="flex gap-1">
                    {policy.isDefault && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Default
                        </span>
                    )}
                    {policy.isActive && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            Active
                        </span>
                    )}
                </div>
            </div>

            <p className="text-sm text-gray-600 mb-3">{policy.description}</p>

            <div className="flex flex-wrap gap-1 mb-3">
                {(policy.associatedProgramTypes || policy.programTypes || []).map(type => (
                    <span key={type} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {type}
                    </span>
                ))}
            </div>

            <div className="grid grid-cols-4 gap-1 text-xs">
                {policy.gradeScale?.slice(0, 4).map((grade, index) => (
                    <div key={index} className="text-center">
                        <div className="font-bold">{grade.alphaGrade || grade.letter}</div>
                        <div className="text-gray-500">{grade.lowPercent ?? grade.minPercentage}%+</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const PolicyPreview = ({ policy, onSetDefault, programDefaults }) => {
        const availablePrograms = policy?.associatedProgramTypes || policy?.programTypes || [];
        const [programTarget, setProgramTarget] = useState(availablePrograms[0] || ProgramTypes[0]);

        useEffect(() => {
            if (!policy) return;
            const nextProgram = (policy.associatedProgramTypes || policy.programTypes || [])[0] || ProgramTypes[0];
            setProgramTarget(nextProgram);
        }, [policy?.id]);

        if (!policy) return null;

        return (
            <div className="bg-white p-6 rounded-lg border">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">{policy.name}</h2>
                        <p className="text-gray-600">{policy.description}</p>
                        <div className="flex gap-2 mt-2">
                            <span className={`px-2 py-1 rounded text-xs ${policy.isHistoric ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {policy.isHistoric ? 'Historic' : 'Current'}
                            </span>
                            {policy.sourceUrl && (
                                <a
                                    href={policy.sourceUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 text-xs underline"
                                >
                                    View official policy
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setEditingPolicy(policy)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Edit Policy"
                        >
                            <Edit3 size={16} />
                        </button>
                        <button
                            onClick={() => downloadPolicy(policy)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Download Policy"
                        >
                            <Download size={16} />
                        </button>
                        {!policy.isDefault && (
                            <button
                                onClick={() => handleDeletePolicy(policy.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Delete Policy"
                            >
                                <Trash2 size={16} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="font-medium mb-3">📊 Grade Scale</h3>
                        <div className="space-y-2">
                            {policy.gradeScale?.map((grade, index) => (
                                <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-lg">{grade.alphaGrade || grade.letter}</span>
                                        <span className="text-sm text-gray-600">
                                            ({grade.lowPercent ?? grade.minPercentage}% - {grade.highPercent ?? grade.maxPercentage}%)
                                        </span>
                                    </div>
                                    <span className="text-sm font-medium">
                                        GPA: {grade.gradePoint ?? grade.gpa}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="font-medium mb-3">🧮 Test Calculator</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Test Percentage:
                                </label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    ref={testGradeInputRef}
                                    value={testGrade}
                                    onChange={(e) => handleTestGradeChange(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter percentage (0-100)"
                                />
                            </div>
                            <button
                                onClick={testGradeCalculation}
                                disabled={!testGrade || !selectedPolicy}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <Calculator size={16} />
                                Calculate Grade
                            </button>

                            {testResult && (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                    {testResult.error ? (
                                        <div className="flex items-center gap-2 text-red-600">
                                            <AlertCircle size={16} />
                                            <span>{testResult.error}</span>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Letter Grade:</span>
                                                <div className="text-xl font-bold">{testResult.data?.letterGrade}</div>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">GPA Points:</span>
                                                <div className="font-medium">{testResult.data?.gpaPoints}</div>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Status:</span>
                                                <div className={`font-medium ${testResult.data?.passingGrade ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                    {testResult.data?.passingGrade ? 'Passing' : 'Failing'}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600">Percentage:</span>
                                                <div className="font-medium">{testResult.data?.percentage}%</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="mt-6 space-y-3">
                                <h4 className="font-medium">🎯 Program defaults</h4>
                                <div className="flex flex-wrap gap-3 items-center">
                                    <select
                                        className="border rounded px-3 py-1 text-sm"
                                        value={programTarget}
                                        onChange={(e) => setProgramTarget(e.target.value)}
                                    >
                                        {availablePrograms.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={() => onSetDefault?.(programTarget, policy.id)}
                                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                                    >
                                        Set as default for {programTarget}
                                    </button>
                                </div>
                                {programTarget && programDefaults?.[programTarget] && (
                                    <p className="text-xs text-gray-600">
                                        Current default: {programDefaults[programTarget] === policy.id ? 'This policy' : programDefaults[programTarget]}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mt-6">
                            <h4 className="font-medium mb-2">📊 Program Types</h4>
                            <div className="flex flex-wrap gap-2">
                                {(policy.associatedProgramTypes || policy.programTypes || []).map(type => (
                                    <span key={type} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                                        {gradingPolicyService.getSupportedProgramTypes()
                                            .find(pt => pt.value === type)?.label || type}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const downloadPolicy = (policy) => {
        const dataStr = JSON.stringify(policy, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

        const exportFileDefaultName = `${policy.name.replace(/\s+/g, '-').toLowerCase()}-policy.json`;

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    const LatePolicyManager = () => {
        const availableLatePolicies = [
            DEFAULT_LATE_POLICY,
            ...customLatePolicies
        ].map(policy => ({
            ...policy,
            penaltiesByDay: normalizePenaltiesByDay(policy)
        }));

        const handleEditLatePolicy = (policy) => {
            setEditingLatePolicy({ ...policy, penaltiesByDay: normalizePenaltiesByDay(policy) });
            setShowLatePolicyForm(true);
        };

        const handleSaveLatePolicy = (policyData) => {
            const normalizedData = {
                ...policyData,
                penaltiesByDay: normalizePenaltiesByDay(policyData)
            };

            if (editingLatePolicy?.id && editingLatePolicy.id !== 'default') {
                updateCustomLatePolicy(editingLatePolicy.id, normalizedData);
            } else {
                saveCustomLatePolicy(normalizedData);
            }
            setShowLatePolicyForm(false);
            setEditingLatePolicy(null);
        };

        const handleDeleteLatePolicy = (policyId) => {
            if (window.confirm('Are you sure you want to delete this late policy?')) {
                deleteCustomLatePolicy(policyId);
                if (currentLatePolicy?.id === policyId) {
                    setCurrentLatePolicy(DEFAULT_LATE_POLICY);
                }
            }
        };

        return (
            <div className="bg-white p-6 rounded-lg border">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <Clock className="text-orange-600" />
                            Late Policy Manager
                        </h2>
                        <p className="text-gray-600 mt-1">
                            Configure penalty multipliers for late submissions
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingLatePolicy(null);
                            setShowLatePolicyForm(true);
                        }}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                    >
                        <Plus size={16} />
                        New Late Policy
                    </button>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableLatePolicies.map(policy => (
                        <div
                            key={policy.id}
                            className={`p-4 border rounded-lg cursor-pointer transition-all ${currentLatePolicy?.id === policy.id
                                ? 'border-orange-500 bg-orange-50 shadow-md'
                                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                }`}
                            onClick={() => setCurrentLatePolicy({ ...policy, penaltiesByDay: normalizePenaltiesByDay(policy) })}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="font-semibold text-gray-800">{policy.name}</h3>
                                <div className="flex gap-1">
                                    {currentLatePolicy?.id === policy.id && (
                                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                            Active
                                        </span>
                                    )}
                                    {policy.id === 'default' && (
                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                            Default
                                        </span>
                                    )}
                                </div>
                            </div>

                            <p className="text-sm text-gray-600 mb-3">{policy.description}</p>

                            <div className="space-y-1 text-xs">
                                {Object.entries(normalizePenaltiesByDay(policy)).map(([day, penalty]) => (
                                    <div key={day} className="flex justify-between">
                                        <span className="text-gray-700">Day {day}</span>
                                        <span className="font-medium">{Math.round(penalty * 100)}% penalty</span>
                                    </div>
                                ))}
                            </div>

                            {policy.id !== 'default' && (
                                <div className="mt-3 flex gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditLatePolicy(policy);
                                        }}
                                        className="flex-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteLatePolicy(policy.id);
                                        }}
                                        className="flex-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                    >
                                        Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {showLatePolicyForm && (
                    <LatePolicyForm
                        policy={{
                            ...DEFAULT_LATE_POLICY,
                            ...editingLatePolicy,
                            penaltiesByDay: normalizePenaltiesByDay(editingLatePolicy || DEFAULT_LATE_POLICY)
                        }}
                        onSave={handleSaveLatePolicy}
                        onCancel={() => {
                            setShowLatePolicyForm(false);
                            setEditingLatePolicy(null);
                        }}
                    />
                )}
            </div>
        );
    };

    const LatePolicyForm = ({ policy, onSave, onCancel }) => {
        const [formData, setFormData] = useState({
            ...policy,
            penaltiesByDay: normalizePenaltiesByDay(policy)
        });

        useEffect(() => {
            setFormData({
                ...policy,
                penaltiesByDay: normalizePenaltiesByDay(policy)
            });
        }, [policy]);

        const addDayPenalty = () => {
            const currentMaxDay = Math.max(...Object.keys(formData.penaltiesByDay || {}).map(Number), 0);
            const nextDay = currentMaxDay + 1;
            setFormData(prev => ({
                ...prev,
                penaltiesByDay: { ...(prev.penaltiesByDay || {}), [nextDay]: 0 },
                maxLateDays: Math.max(prev.maxLateDays || nextDay, nextDay)
            }));
        };

        const updateDayPenalty = (day, penalty) => {
            const numericDay = Number(day);
            if (Number.isNaN(numericDay) || numericDay <= 0) return;

            const safePenalty = Math.min(Math.max(Number(penalty) || 0, 0), 1);

            setFormData(prev => ({
                ...prev,
                penaltiesByDay: {
                    ...(prev.penaltiesByDay || {}),
                    [numericDay]: safePenalty
                }
            }));
        };

        const removeDayPenalty = (day) => {
            setFormData(prev => {
                const updated = { ...(prev.penaltiesByDay || {}) };
                delete updated[day];
                const remaining = Object.keys(updated).map(Number);
                const newMax = remaining.length ? Math.max(...remaining) : DEFAULT_LATE_POLICY.maxLateDays;
                return {
                    ...prev,
                    penaltiesByDay: updated,
                    maxLateDays: newMax
                };
            });
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-2xl w-full p-6 overflow-y-auto max-h-[90vh]">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold">{formData.id ? 'Edit' : 'Create'} Late Policy</h2>
                        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Policy Name</label>
                            <input
                                type="text"
                                value={formData.name || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="e.g., Standard Late Policy"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                value={formData.description || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                rows="2"
                                placeholder="Describe this late policy..."
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <label className="block text-sm font-medium text-gray-700">Day-by-day penalties</label>
                                <button
                                    onClick={addDayPenalty}
                                    className="px-3 py-1 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm flex items-center gap-1"
                                >
                                    <Plus size={14} />
                                    Add Day
                                </button>
                            </div>

                            <div className="space-y-3">
                                {Object.entries(formData.penaltiesByDay || {}).map(([day, penalty]) => (
                                    <div key={day} className="p-3 border border-gray-200 rounded-lg">
                                        <div className="grid grid-cols-5 gap-3 items-center">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Day</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={day}
                                                    onChange={(e) => {
                                                        const newDay = Number(e.target.value);
                                                        if (Number.isNaN(newDay) || newDay <= 0) return;
                                                        setFormData(prev => {
                                                            const updated = { ...(prev.penaltiesByDay || {}) };
                                                            delete updated[day];
                                                            updated[newDay] = penalty;
                                                            return { ...prev, penaltiesByDay: updated };
                                                        });
                                                    }}
                                                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-orange-500"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Penalty (0-1)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="1"
                                                    step="0.01"
                                                    value={penalty}
                                                    onChange={(e) => updateDayPenalty(day, e.target.value)}
                                                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-orange-500"
                                                    placeholder="0.2"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Max Late Days</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={formData.maxLateDays || ''}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        maxLateDays: Math.max(Number(e.target.value) || 1, 1)
                                                    }))}
                                                    className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-orange-500"
                                                />
                                            </div>
                                            <div className="flex justify-end">
                                                <button
                                                    onClick={() => removeDayPenalty(day)}
                                                    className="px-2 py-1 text-red-600 hover:text-red-800 text-sm"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button
                            onClick={() => onSave(formData)}
                            className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2"
                        >
                            <Save size={16} />
                            Save Policy
                        </button>
                        <button
                            onClick={onCancel}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // New Percentage Calculators Component with its own state
    const PercentageCalculators = ({ gradeBook, classList, sharedRubric }) => {
        // Calculator state moved to this component to prevent parent re-renders
        const [calc1Number, setCalc1Number] = useState('');
        const [calc1Percentage, setCalc1Percentage] = useState('');
        const [calc1Result, setCalc1Result] = useState(null);

        const [calc2Number1, setCalc2Number1] = useState('');
        const [calc2Number2, setCalc2Number2] = useState('');
        const [calc2Result, setCalc2Result] = useState(null);

        const [calc3Original, setCalc3Original] = useState('');
        const [calc3New, setCalc3New] = useState('');
        const [calc3Result, setCalc3Result] = useState(null);
        const [activeCalculatorTab, setActiveCalculatorTab] = useState('percentage');

        useEffect(() => {
            const storedTab = sessionStorage.getItem(POLICY_MANAGER_CALCULATOR_TAB_KEY);
            if (storedTab === 'what-if') {
                setActiveCalculatorTab('what-if');
                sessionStorage.removeItem(POLICY_MANAGER_CALCULATOR_TAB_KEY);
            }
        }, []);

        // Input handlers - now scoped to this component only
        const handleCalc1PercentageChange = useCallback((e) => {
            setCalc1Percentage(e.target.value);
        }, []);

        const handleCalc1NumberChange = useCallback((e) => {
            setCalc1Number(e.target.value);
        }, []);

        const handleCalc2Number1Change = useCallback((e) => {
            setCalc2Number1(e.target.value);
        }, []);

        const handleCalc2Number2Change = useCallback((e) => {
            setCalc2Number2(e.target.value);
        }, []);

        const handleCalc3OriginalChange = useCallback((e) => {
            setCalc3Original(e.target.value);
        }, []);

        const handleCalc3NewChange = useCallback((e) => {
            setCalc3New(e.target.value);
        }, []);

        // Calculator functions - now scoped to this component only
        const calculatePercentageOfNumber = useCallback(() => {
            const number = parseFloat(calc1Number);
            const percentage = parseFloat(calc1Percentage);

            if (isNaN(number) || isNaN(percentage)) {
                setCalc1Result({ error: 'Please enter valid numbers' });
                return;
            }

            const result = (percentage / 100) * number;
            setCalc1Result({
                result: result,
                calculation: `${percentage}% of ${number} = ${result}`,
                formula: `(${percentage} ÷ 100) × ${number} = ${result}`
            });
        }, [calc1Number, calc1Percentage]);

        const calculateNumberAsPercentage = useCallback(() => {
            const number1 = parseFloat(calc2Number1);
            const number2 = parseFloat(calc2Number2);

            if (isNaN(number1) || isNaN(number2) || number2 === 0) {
                setCalc2Result({ error: 'Please enter valid numbers (second number cannot be zero)' });
                return;
            }

            const result = (number1 / number2) * 100;
            setCalc2Result({
                result: result,
                calculation: `${number1} is ${result.toFixed(2)}% of ${number2}`,
                formula: `(${number1} ÷ ${number2}) × 100 = ${result.toFixed(2)}%`
            });
        }, [calc2Number1, calc2Number2]);

        const calculatePercentageChange = useCallback(() => {
            const original = parseFloat(calc3Original);
            const newValue = parseFloat(calc3New);

            if (isNaN(original) || isNaN(newValue) || original === 0) {
                setCalc3Result({ error: 'Please enter valid numbers (original number cannot be zero)' });
                return;
            }

            const change = newValue - original;
            const percentageChange = (change / original) * 100;
            const isIncrease = change > 0;

            setCalc3Result({
                result: Math.abs(percentageChange),
                change: change,
                isIncrease: isIncrease,
                calculation: `${isIncrease ? 'Increase' : 'Decrease'} of ${Math.abs(percentageChange).toFixed(2)}%`,
                formula: `((${newValue} - ${original}) ÷ ${original}) × 100 = ${percentageChange.toFixed(2)}%`,
                details: {
                    original: original,
                    new: newValue,
                    difference: change,
                    percentageChange: percentageChange
                }
            });
        }, [calc3Original, calc3New]);

        return (
            <div className="space-y-8">
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveCalculatorTab('percentage')}
                        className={`px-6 py-3 font-medium text-sm border-b-2 transition-all ${activeCalculatorTab === 'percentage'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Calculator size={16} />
                            Percentage Calculators
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveCalculatorTab('what-if')}
                        className={`px-6 py-3 font-medium text-sm border-b-2 transition-all ${activeCalculatorTab === 'what-if'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Calculator size={16} />
                            What-If Calculator
                        </div>
                    </button>
                </div>

                {activeCalculatorTab === 'what-if' ? (
                    <WhatIfCalculator
                        gradeBook={gradeBook}
                        classList={classList}
                        sharedRubric={sharedRubric}
                    />
                ) : (
                    <>
                {/* Calculator 1: Percentage of a Number */}
                <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Percent className="text-blue-600" />
                        Percentage of a Number
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Calculate what percentage of a number equals. For example: What is 25% of 200?
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Percentage (%)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={calc1Percentage}
                                    onChange={handleCalc1PercentageChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter percentage (e.g., 25)"
                                    autoComplete="off"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Number
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={calc1Number}
                                    onChange={handleCalc1NumberChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter number (e.g., 200)"
                                    autoComplete="off"
                                />
                            </div>
                            <button
                                onClick={calculatePercentageOfNumber}
                                disabled={!calc1Percentage || !calc1Number}
                                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <Calculator size={16} />
                                Calculate
                            </button>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                            {calc1Result ? (
                                calc1Result.error ? (
                                    <div className="flex items-center gap-2 text-red-600">
                                        <AlertCircle size={16} />
                                        <span>{calc1Result.error}</span>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="text-center">
                                            <div className="text-3xl font-bold text-blue-600">
                                                {calc1Result.result}
                                            </div>
                                            <div className="text-lg text-gray-700 mt-2">
                                                {calc1Result.calculation}
                                            </div>
                                        </div>
                                        <div className="border-t pt-3">
                                            <h4 className="font-medium text-gray-700 mb-1">Formula:</h4>
                                            <div className="text-sm text-gray-600 font-mono bg-white p-2 rounded">
                                                {calc1Result.formula}
                                            </div>
                                        </div>
                                    </div>
                                )
                            ) : (
                                <div className="text-center text-gray-500">
                                    <Calculator className="mx-auto h-12 w-12 mb-2 opacity-50" />
                                    <p>Enter values and click Calculate to see the result</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Calculator 2: One Number as Percentage of Another */}
                <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Percent className="text-green-600" />
                        One Number as Percentage of Another
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Find what percentage one number is of another. For example: What percentage is 50 of 200?
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    First Number (Part)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={calc2Number1}
                                    onChange={handleCalc2Number1Change}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    placeholder="Enter first number (e.g., 50)"
                                    autoComplete="off"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Second Number (Total)
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={calc2Number2}
                                    onChange={handleCalc2Number2Change}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    placeholder="Enter second number (e.g., 200)"
                                    autoComplete="off"
                                />
                            </div>
                            <button
                                onClick={calculateNumberAsPercentage}
                                disabled={!calc2Number1 || !calc2Number2}
                                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <Calculator size={16} />
                                Calculate
                            </button>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                            {calc2Result ? (
                                calc2Result.error ? (
                                    <div className="flex items-center gap-2 text-red-600">
                                        <AlertCircle size={16} />
                                        <span>{calc2Result.error}</span>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="text-center">
                                            <div className="text-3xl font-bold text-green-600">
                                                {calc2Result.result.toFixed(2)}%
                                            </div>
                                            <div className="text-lg text-gray-700 mt-2">
                                                {calc2Result.calculation}
                                            </div>
                                        </div>
                                        <div className="border-t pt-3">
                                            <h4 className="font-medium text-gray-700 mb-1">Formula:</h4>
                                            <div className="text-sm text-gray-600 font-mono bg-white p-2 rounded">
                                                {calc2Result.formula}
                                            </div>
                                        </div>
                                    </div>
                                )
                            ) : (
                                <div className="text-center text-gray-500">
                                    <Calculator className="mx-auto h-12 w-12 mb-2 opacity-50" />
                                    <p>Enter values and click Calculate to see the result</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Calculator 3: Percentage Increase/Decrease */}
                <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Percent className="text-purple-600" />
                        Percentage Increase/Decrease
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Calculate the percentage change between two numbers. For example: What's the percentage increase from 100 to 150?
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Original Number
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={calc3Original}
                                    onChange={handleCalc3OriginalChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="Enter original number (e.g., 100)"
                                    autoComplete="off"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    New Number
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={calc3New}
                                    onChange={handleCalc3NewChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="Enter new number (e.g., 150)"
                                    autoComplete="off"
                                />
                            </div>
                            <button
                                onClick={calculatePercentageChange}
                                disabled={!calc3Original || !calc3New}
                                className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <Calculator size={16} />
                                Calculate
                            </button>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                            {calc3Result ? (
                                calc3Result.error ? (
                                    <div className="flex items-center gap-2 text-red-600">
                                        <AlertCircle size={16} />
                                        <span>{calc3Result.error}</span>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="text-center">
                                            <div className={`text-3xl font-bold ${calc3Result.isIncrease ? 'text-green-600' : 'text-red-600'}`}>
                                                {calc3Result.isIncrease ? '+' : '-'}{calc3Result.result.toFixed(2)}%
                                            </div>
                                            <div className="text-lg text-gray-700 mt-2">
                                                {calc3Result.calculation}
                                            </div>
                                        </div>
                                        <div className="border-t pt-3 space-y-2">
                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <span className="text-gray-600">Original:</span>
                                                    <div className="font-medium">{calc3Result.details.original}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">New:</span>
                                                    <div className="font-medium">{calc3Result.details.new}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Difference:</span>
                                                    <div className={`font-medium ${calc3Result.isIncrease ? 'text-green-600' : 'text-red-600'}`}>
                                                        {calc3Result.change > 0 ? '+' : ''}{calc3Result.change}
                                                    </div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Change:</span>
                                                    <div className={`font-medium ${calc3Result.isIncrease ? 'text-green-600' : 'text-red-600'}`}>
                                                        {calc3Result.details.percentageChange.toFixed(2)}%
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="border-t pt-2">
                                                <h4 className="font-medium text-gray-700 mb-1">Formula:</h4>
                                                <div className="text-sm text-gray-600 font-mono bg-white p-2 rounded">
                                                    {calc3Result.formula}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            ) : (
                                <div className="text-center text-gray-500">
                                    <Calculator className="mx-auto h-12 w-12 mb-2 opacity-50" />
                                    <p>Enter values and click Calculate to see the result</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                    </>
                )}
            </div>
        );
    };

    const newPolicyTemplate = {
        name: '',
        description: '',
        associatedProgramTypes: [],
        gradeScale: [
            { alphaGrade: 'A+', gradePoint: 4.0, lowPercent: 90, highPercent: 100, description: '' }
        ],
        isActive: true,
        isDefault: false
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading grading policies...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6">
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Settings className="text-blue-600" />
                            Policy Manager
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Manage grading policies and use calculators
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Tab Navigation */}
            <div className="mb-6">
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveMainTab('policies')}
                        className={`px-6 py-3 font-medium text-sm border-b-2 transition-all ${activeMainTab === 'policies'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Settings size={16} />
                            Grading Policies
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveMainTab('calculators')}
                        className={`px-6 py-3 font-medium text-sm border-b-2 transition-all ${activeMainTab === 'calculators'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Calculator size={16} />
                            Calculators
                        </div>
                    </button>
                </div>
            </div>

            {/* Policy Form Modal */}
            {(showCreateForm || editingPolicy) && (
                <PolicyForm
                    policy={editingPolicy || newPolicyTemplate}
                    onSave={handleSavePolicy}
                    onCancel={() => {
                        setShowCreateForm(false);
                        setEditingPolicy(null);
                    }}
                />
            )}

            {/* Content based on active tab */}
            {activeMainTab === 'policies' ? (
                <div>
                    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                        <h3 className="font-semibold mb-2 flex items-center gap-2">
                            <Info size={16} /> Using the Policy Manager
                        </h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Create or edit a grading policy, then select it to view the grading scale.</li>
                            <li>Use <strong>Set as default for Program</strong> to tie a program type to that policy. The Class List Manager will automatically display and apply that policy when the matching program type is chosen.</li>
                            <li>Defaults and grading scales are saved locally, so everything is manual—no external API is required.</li>
                        </ul>
                    </div>

                    <div className="mb-6">
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                        >
                            <Plus size={16} />
                            New Policy
                        </button>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Policy List */}
                        <div className="lg:col-span-1">
                            <div className="flex items-center justify-between gap-2 mb-4">
                                <h2 className="text-lg font-semibold">Available Policies</h2>
                                <select
                                    className="border rounded px-2 py-1 text-sm"
                                    value={programTypeFilter}
                                    onChange={(e) => setProgramTypeFilter(e.target.value)}
                                >
                                    <option value="All programs">All programs</option>
                                    {ProgramTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-3">
                                {filteredPolicies.map(policy => (
                                    <PolicyCard
                                        key={policy.id}
                                        policy={policy}
                                        isSelected={selectedPolicy?.id === policy.id}
                                        onClick={() => handlePolicySelect(policy)}
                                    />
                                ))}
                                {filteredPolicies.length === 0 && (
                                    <div className="text-center p-8 border-2 border-dashed border-gray-300 rounded-lg">
                                        <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                        <p className="text-gray-600">No policies found</p>
                                        <p className="text-sm text-gray-500">Create your first grading policy</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Policy Preview */}
                        <div className="lg:col-span-2">
                            {selectedPolicy ? (
                                <PolicyPreview
                                    policy={selectedPolicy}
                                    onSetDefault={handleSetDefaultForProgram}
                                    programDefaults={programDefaults}
                                />
                            ) : (
                                <div className="bg-white p-12 rounded-lg border border-gray-200">
                                    <div className="text-center">
                                        <Eye className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                                        <h3 className="text-lg font-medium text-gray-800 mb-2">Select a Policy</h3>
                                        <p className="text-gray-600">Choose a policy from the list to view details and test calculations</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Late Policy Manager Section */}
                    <div className="mt-12">
                        <LatePolicyManager />
                    </div>

                    {/* Course-specific late policy editor */}
                    <div className="mt-12">
                        <CourseSettings
                            embedded
                            title="Course-Specific Late Policy Editor"
                        />
                    </div>

                    {/* Development Mode Notice */}
                    {process.env.NODE_ENV === 'development' && (
                        <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-center gap-2 text-yellow-800">
                                <AlertCircle size={16} />
                                <span className="font-medium">Development Mode</span>
                            </div>
                            <p className="text-sm text-yellow-700 mt-1">
                                Grading policies, defaults, and calculations are stored locally so you can manage them manually. No external API connection is required for alignment with the Class List Manager.
                            </p>
                        </div>
                    )}
                </div>
            ) : (
                /* Percentage Calculators Tab */
                <PercentageCalculators
                    gradeBook={gradeBook}
                    classList={classList}
                    sharedRubric={sharedRubric}
                />
            )}
        </div>
    );
};

export default GradingPolicyManager;
